if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer') && !this.closest('cart-complementary-products')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.cart?.isAdding || this.cart?.isUpdatingQuantity || this.cart?.isUpdatingDiscount || this.cart?.discountRefreshFailed || this.cart?.quantityRefreshFailed) return;
        if (this.closest('cart-complementary-products') && this.cart?.tagName === 'CART-DRAWER') {
          this.onRecommendationSubmit(evt);
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const accessoryOffer = document.querySelector('arc-accessory-offer');
        const accessoryItems = accessoryOffer?.itemsFor?.(this.form) || [];
        const offerTracking = accessoryItems.length ? accessoryOffer.trackingContext?.() || {} : {};
        if (accessoryItems.length) {
          for (const [name, value] of [...formData.entries()]) {
            if (name === 'id' || name === 'quantity' || name === 'selling_plan' || name.startsWith('properties[')) {
              formData.delete(name);
              const nested = name.startsWith('properties[') ? `items[0][properties]${name.slice(10)}` : `items[0][${name}]`;
              formData.append(nested, value);
            }
          }
          accessoryItems.forEach((item, index) => {
            formData.append(`items[${index + 1}][id]`, item.id);
            formData.append(`items[${index + 1}][quantity]`, item.quantity);
          });
        }
        const quantity = parseInt(formData.get('quantity') || formData.get('items[0][quantity]')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        if (!this.closest('quick-add-modal')) this.cart?.beginAdd?.(accessoryItems.length ? null : this.getPendingCartItem(variantId, quantity, formData));

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.cart?.cancelAdd?.();
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (accessoryItems.length) accessoryOffer.completeAdd();
            window.PLInteractions?.added(this, response, quantity, offerTracking);

            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response.items?.length ? { ...response, ...response.items[0] } : response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response.items?.length ? { ...response, ...response.items[0] } : response);
              });
            }
          })
          .catch((e) => {
            this.cart?.cancelAdd?.();
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.cart?.endAdd?.();
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.tagName !== 'CART-DRAWER' && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async onRecommendationSubmit(evt) {
        this.handleErrorMessage();
        const originalText = this.submitButtonText.textContent;
        const anchor = this.closest('[data-recommended-product-id]');
        const picker = this.closest('product-info-complete')?.querySelector('variant-selects');
        const pickerWasInert = picker?.inert;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButtonText.setAttribute('aria-live', 'polite');
        this.submitButtonText.textContent = this.submitButton.dataset.addingLabel;
        this.submitButton.dataset.cartAddState = 'adding';
        if (picker) picker.inert = true;
        this.cart.beginRecommendationAdd();
        const data = new FormData(this.form);
        data.append('sections', this.cart.getSectionsToRender().map(section => section.id));
        data.append('sections_url', window.location.pathname);
        const variantId = data.get('id');
        const quantity = parseInt(data.get('quantity')) || 1;
        const deferred = this.createCartLinesUpdateEvent(variantId, quantity);
        let confirmed = false;
        let refreshed = false;
        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = data;
          const request = await fetch(routes.cart_add_url, config);
          const response = await request.json();
          if (!request.ok || response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form', productVariantId: variantId,
              errors: response.errors || response.description, message: response.message,
            });
            throw Object.assign(new Error(response.description || response.message || window.cartStrings.error), { code: 'INVALID' });
          }
          confirmed = true;
          window.PLInteractions?.added(this, response, quantity);
          this.resolveCartLinesUpdate(deferred);
          await this.cart.renderRecommendationContents(response, anchor);
          refreshed = true;
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form', productVariantId: variantId, cartData: response,
          });
          this.submitButtonText.textContent = this.submitButton.dataset.addedLabel;
          this.submitButton.dataset.cartAddState = 'added';
          clearTimeout(this.recommendationResetTimer);
          this.recommendationResetTimer = setTimeout(() => {
            if (!this.isConnected) return;
            if (this.submitButtonText.textContent === this.submitButton.dataset.addedLabel) {
              this.submitButtonText.textContent = originalText;
            }
            delete this.submitButton.dataset.cartAddState;
            this.submitButton.removeAttribute('aria-disabled');
          }, 2000);
        } catch (error) {
          const message = confirmed ? this.submitButton.dataset.refreshErrorLabel : error.message || window.cartStrings.error;
          this.handleErrorMessage(message);
          this.dispatchCartErrorEvent(message, confirmed ? 'CART_REFRESH_FAILED' : error.code || 'SERVICE_UNAVAILABLE');
          if (!confirmed) deferred?.reject(error);
          this.submitButtonText.textContent = confirmed ? this.submitButton.dataset.addedLabel : originalText;
          delete this.submitButton.dataset.cartAddState;
          if (!confirmed) this.submitButton.removeAttribute('aria-disabled');
        } finally {
          if (picker) picker.inert = pickerWasInert;
          this.cart.endRecommendationAdd(confirmed && !refreshed);
          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      getPendingCartItem(variantId, quantity, formData) {
        const scope = this.closest('product-info-complete, product-info, product-component') || this.parentElement;
        let variant;
        const sources = [...scope.querySelectorAll('[data-selected-variant]')].map((node) => node.textContent);
        sources.push(this.dataset.cartVariant);
        for (const source of sources) {
          try {
            const candidate = JSON.parse(source);
            if (String(candidate?.id) === String(variantId)) { variant = candidate; break; }
          } catch {}
        }
        if (!variant || !this.dataset.cartTitle || !Number.isFinite(Number(variant.price))) return null;
        let optionNames = [];
        try { optionNames = JSON.parse(this.dataset.cartOptions || '[]'); } catch {}
        const customized = [...formData.entries()].some(([name, value]) =>
          (name.startsWith('properties[') || name === 'selling_plan') && String(value).trim()
        );
        return {
          variantId: String(variantId), quantity, customized,
          title: this.dataset.cartTitle, url: this.dataset.cartUrl,
          image: variant.featured_image?.src || this.dataset.cartImage,
          price: variant.price, currency: this.dataset.cartCurrency || 'USD',
          options: (variant.options || []).map((value, index) => ({
            name: optionNames[index],
            value: optionNames[index] === 'Size' ? formData.get('properties[Size]') || value : value,
          })),
        };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.submitButton.toggleAttribute('disabled', disable);
        const label = disable ? text : window.variantStrings.addToCart;
        if (label && this.submitButtonText.textContent.trim().toLowerCase() !== label.trim().toLowerCase()) {
          this.submitButtonText.textContent = label;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData(true)
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        if (this.cart) this.cart.confirmedCartDataPromise = pendingCartDataPromise;
        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        window.PLInteractions?.shoppingError?.(this, code, code === 'CART_REFRESH_FAILED' ? 'cart_refresh' : 'add');
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
